import { useCallback, useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import clsx from 'clsx'
import { QuizPreviewPanel } from './QuizPreviewPanel'

interface QuizSequenceInspectorProps {
  quizIds: string[]
  sequenceKey: string | number
}

export function QuizSequenceInspector({ quizIds, sequenceKey }: QuizSequenceInspectorProps) {
  const total = quizIds.length
  const [current, setCurrent] = useState(0)

  useEffect(() => {
    setCurrent(0)
  }, [sequenceKey])

  const goPrev = useCallback(() => setCurrent((index) => Math.max(0, index - 1)), [])
  const goNext = useCallback(
    () => setCurrent((index) => Math.min(total - 1, index + 1)),
    [total],
  )

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (
        target &&
        (target.isContentEditable ||
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT')
      ) {
        return
      }

      if (event.key === 'ArrowLeft') goPrev()
      if (event.key === 'ArrowRight') goNext()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [goNext, goPrev])

  if (total === 0) {
    return (
      <div className="py-12 text-sm text-center border border-dashed rounded-lg border-slate-300 bg-slate-50 text-slate-400">
        문제가 없습니다
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex flex-wrap gap-1.5 min-w-0">
          {quizIds.map((quizId, index) => (
            <button
              key={`${quizId}-${index}`}
              type="button"
              onClick={() => setCurrent(index)}
              aria-label={`문제 ${index + 1}`}
              aria-current={index === current ? 'step' : undefined}
              className={clsx(
                'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2',
                index === current
                  ? 'border-amber-500 bg-amber-500 text-white'
                  : 'border-slate-300 bg-white text-slate-600 hover:border-amber-400 hover:bg-amber-50',
              )}
            >
              {index + 1}
            </button>
          ))}
        </div>
        <span className="ml-auto shrink-0 text-sm tabular-nums text-slate-500">
          {current + 1} / {total}
        </span>
      </div>

      <div className="p-3 bg-white border rounded-lg border-slate-200">
        <QuizPreviewPanel quizId={quizIds[current]} />
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={goPrev}
          disabled={current === 0}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft className="w-4 h-4" />
          이전
        </button>
        <button
          type="button"
          onClick={goNext}
          disabled={current >= total - 1}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40"
        >
          다음
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
